import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Report from "@/services/analytics/report";

export interface AnalyticReportsParams {
  organizationId: string;
  startDate: string;
  endDate: string;
  createdById?: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useAnalyticReports(
  params: Ref<AnalyticReportsParams | null> | AnalyticReportsParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["analyticReports", "disabled"];
    return ["analyticReports", p.organizationId, p.startDate, p.endDate, p.createdById || ""];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId && !!p.startDate && !!p.endDate;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: Record<string, any> = {
        organizationId: p.organizationId,
        startDate: p.startDate,
        endDate: p.endDate,
      };

      if (p.createdById) {
        queryParams.createdById = p.createdById;
      }

      const result = await Report.getAnalyticReports(queryParams);

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load analytic reports");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
